import type {ReactNode, ErrorInfo} from "react";
import {Component} from "react";
import AppShell from "../shared/ui/AppShell";
import {redTheme} from "../styles/themes/color-themes.css";
import {Col, css, Text} from "../ui/Box";

interface Props {
  children: ReactNode;
}

interface State {
  caughtError: Error | null;
  errorInfo: ErrorInfo | null;
}

const RenderError = ({error, info}: {error: Error; info: ErrorInfo | null}) => (
  <AppShell>
    <Col pa="16" sp="24">
      <Col className={redTheme} bg="surface" color="primary" rounded="4" pa="12" sp="12">
        <Text bold size="16">
          {error.name}: {error.message}
        </Text>
        {error.stack ? <pre className={css({overflowX: "auto"})}>{error.stack}</pre> : null}
      </Col>
      {info && (
        <Col sp="12">
          <Text size="16" bold>
            Error happened in:
          </Text>
          <pre className={css({overflowX: "auto"})}>{info.componentStack?.trim()}</pre>
        </Col>
      )}
    </Col>
  </AppShell>
);

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {caughtError: null, errorInfo: null};
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return {caughtError: error, errorInfo: null};
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Example "componentStack":
    //   in ComponentThatThrows (created by App)
    //   in ErrorBoundary (created by App)
    //   in div (created by App)
    //   in App
    console.error(error, info.componentStack);
    this.setState({errorInfo: info});
  }

  render(): ReactNode {
    if (this.state.caughtError) {
      return <RenderError error={this.state.caughtError} info={this.state.errorInfo} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
